/**
 * EncounterContextPills — Feb 28, 2026
 *
 * Thin pill-strip that lives at the TOP of the provider input column. Each
 * pill represents a piece of pre-encounter context the provider may want
 * to capture/review BEFORE dictating into the main notes box. Tapping a
 * pill expands its lightweight textarea inline beneath the strip; tapping
 * again (or another pill) collapses it.
 *
 * Replaces the previous full-width `<details>` blocks for "At Your Last
 * Visit" and "Pre-Visit Prep / RN Handoff" which felt heavy and pushed
 * the dictation input far down the page. User feedback (Feb 28, 2026):
 *
 *   "the at your last visit and previsit prep those collapsible lines
 *    need to be above the input below the maybe included possibly in
 *    the header cuz it is thin and sleek"
 *   "the pick up the baton button is not as understanding as what it's
 *    supposed to be... if we're going to pick up the baton then need to
 *    be next to the baton but pretty pill"
 *
 * Also exposes the "Pick Up Baton / Merge" action as a sleek pill so the
 * relay-race workflow is discoverable from the header.
 */
import React, { useState } from 'react';
import { ClipboardList, Handshake, GitMerge, ChevronUp } from 'lucide-react';

const ContextPillTextarea = ({
  open, value, setValue, placeholder, accent, testId,
}) => {
  if (!open) return null;
  const palette = {
    blue: 'border-blue-500/30 bg-blue-500/[0.04] focus:border-blue-400/60',
    purple: 'border-purple-500/30 bg-purple-500/[0.04] focus:border-purple-400/60',
  }[accent] || 'border-border bg-secondary/30';
  return (
    <textarea
      value={value || ''}
      onChange={(e) => setValue(e.target.value)}
      placeholder={placeholder}
      className={`mt-1 w-full min-h-[88px] text-xs p-2 rounded border ${palette} focus:outline-none resize-y`}
      data-testid={testId}
    />
  );
};

const Pill = ({
  active, onClick, icon: Icon, label, badge, accent, testId, title,
}) => {
  const baseAccent = {
    blue:
      active
        ? 'bg-blue-500/20 border-blue-400/60 text-blue-200 ring-1 ring-blue-400/40'
        : 'bg-secondary/40 border-border text-muted-foreground hover:bg-blue-500/10 hover:border-blue-500/40 hover:text-blue-300',
    purple:
      active
        ? 'bg-purple-500/20 border-purple-400/60 text-purple-200 ring-1 ring-purple-400/40'
        : 'bg-secondary/40 border-border text-muted-foreground hover:bg-purple-500/10 hover:border-purple-500/40 hover:text-purple-300',
    amber:
      active
        ? 'bg-amber-500/20 border-amber-400/60 text-amber-200 ring-1 ring-amber-400/40'
        : 'bg-secondary/40 border-border text-muted-foreground hover:bg-amber-500/10 hover:border-amber-500/40 hover:text-amber-300',
  }[accent] || 'bg-secondary/40 border-border text-muted-foreground hover:bg-foreground/5';

  return (
    <button
      type="button"
      onClick={onClick}
      title={title || label}
      data-testid={testId}
      data-state={active ? 'active' : 'inactive'}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium leading-tight transition-all whitespace-nowrap ${baseAccent}`}
    >
      <Icon className="w-3 h-3" />
      <span>{label}</span>
      {badge && (
        <span className="ml-0.5 px-1 rounded bg-foreground/15 text-[9px] font-bold">
          {badge}
        </span>
      )}
      {active && <ChevronUp className="w-3 h-3 opacity-70" />}
    </button>
  );
};

export const EncounterContextPills = ({
  priorVisitContext, setPriorVisitContext,
  prepNotes, setPrepNotes,
  onPickUpBaton,
}) => {
  const [open, setOpen] = useState(() => {
    // Auto-open whichever context has content so the user lands on the
    // pre-populated textarea on subsequent renders.
    if ((prepNotes || '').trim()) return 'prep';
    if ((priorVisitContext || '').trim()) return 'prior';
    return null;
  });

  const toggle = (key) => setOpen((cur) => (cur === key ? null : key));

  return (
    <div
      className="mb-2 pb-2 border-b border-border/40"
      data-testid="encounter-context-pills"
    >
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="elegant-label text-[10px]">
          Pre-Encounter:
        </span>
        <Pill
          active={open === 'prior'}
          onClick={() => toggle('prior')}
          icon={ClipboardList}
          label="Last Visit"
          accent="blue"
          badge={priorVisitContext ? `${priorVisitContext.length}` : null}
          title="At Your Last Visit — historical context; never woven into today's HPI/A/P"
          testId="context-pill-last-visit"
        />
        <Pill
          active={open === 'prep'}
          onClick={() => toggle('prep')}
          icon={Handshake}
          label="Pre-Visit Prep"
          accent="purple"
          badge={prepNotes ? `${prepNotes.length}` : null}
          title="Pre-Visit Prep / RN Handoff — captured before walking in"
          testId="context-pill-prep"
        />
        <Pill
          active={false}
          onClick={() => { if (typeof onPickUpBaton === 'function') onPickUpBaton(); }}
          icon={GitMerge}
          label="Pick Up Baton"
          accent="amber"
          title="Open the merge modal — paste teammate notes / Genesis output to continue an in-progress visit"
          testId="context-pill-pickup-baton"
        />
      </div>
      <ContextPillTextarea
        open={open === 'prior'}
        value={priorVisitContext}
        setValue={setPriorVisitContext}
        accent="blue"
        testId="context-pill-textarea-last-visit"
        placeholder={`Paste prior A/P, education goals, barriers, social-hx context — historical only.
e.g. Last visit: BMI 32, started semaglutide 0.25 mg, follow up 4 wks. Goal: walk 3x/wk x 20 min.`}
      />
      <ContextPillTextarea
        open={open === 'prep'}
        value={prepNotes}
        setValue={setPrepNotes}
        accent="purple"
        testId="context-pill-textarea-prep"
        placeholder={`Capture BEFORE walking in — RN report, chart review, prep thoughts. Treated as HANDOFF, not as direct exam findings.
e.g. RN: BP 152/94. Prior chart: A1c 7.4. Plan: BMP + lipids. Be empathetic re: compliance.`}
      />
    </div>
  );
};

export default EncounterContextPills;
